import React, { useState, useRef, useEffect } from "react";

/* ─── Config ─── */
const API           = process.env.REACT_APP_API_URL || "/api";
const POLL_INTERVAL = 3000;
const MAX_WAIT      = 300000; // 5 min

/* ─── Auth fetch ─── */
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("token");
  return fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
};

/* ─── Status config ─── */
const STATUS_CONFIG = {
  idle:       { color: "text-gray-400",    bg: "",                                  icon: ""  },
  preparing:  { color: "text-blue-600",    bg: "bg-blue-50 border-blue-100",        icon: "⏳" },
  fetching:   { color: "text-blue-600",    bg: "bg-blue-50 border-blue-100",        icon: "⏳" },
  processing: { color: "text-amber-600",   bg: "bg-amber-50 border-amber-100",      icon: "⚙️" },
  ready:      { color: "text-emerald-600", bg: "bg-emerald-50 border-emerald-100",  icon: "✓"  },
  failed:     { color: "text-red-600",     bg: "bg-red-50 border-red-100",          icon: "✕"  },
  timeout:    { color: "text-red-600",     bg: "bg-red-50 border-red-100",          icon: "✕"  },
};

const STATUS_LABELS = {
  preparing:  "Preparing report…",
  fetching:   "Fetching data…",
  processing: "Generating CSV with Athena - this may take a moment",
  ready:      "Download started",
  failed:     "Download failed. Please try again.",
  timeout:    "Request timed out. Please try again.",
};

/* ════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════ */
const AdditionalTab = ({ deviceId }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate,   setEndDate]   = useState("");
  const [loading,   setLoading]   = useState(false);
  const [statusKey, setStatusKey] = useState("idle");
  const pollRef = useRef(null);

  /* ── Validation ── */
  const isValid =
    deviceId &&
    startDate &&
    endDate &&
    new Date(startDate) <= new Date(endDate);

  /* ── Range check ── */
  const isSmallRange = (s, e) =>
    (new Date(e) - new Date(s)) / (1000 * 60 * 60 * 24) <= 3;

  /* ── Today for max date ── */
  const today = new Date().toISOString().split("T")[0];

  /* ── Polling ── */
  const pollStatus = (reportId) =>
    new Promise((resolve, reject) => {
      const startTime = Date.now();

      pollRef.current = setInterval(async () => {
        // ✅ Timeout checked at top of each tick — catches hangs regardless of fetch duration
        if (Date.now() - startTime > MAX_WAIT) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          reject("timeout");
          return;
        }

        try {
          const res  = await authFetch(`${API}/reports/status/${reportId}`);

          // ✅ Guard non-ok responses — avoids JSON parse errors on 401/500
          if (!res.ok) return; // keep polling; might be a transient error

          const data = await res.json();

          if (data.status === "processing") setStatusKey("processing");

          if (data.status === "completed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            resolve(reportId);
          }

          if (data.status === "failed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            reject("failed");
          }
        } catch {
          // network hiccup — keep polling until MAX_WAIT
        }
      }, POLL_INTERVAL);
    });

  /* ── Download trigger ── */
  const triggerDownload = (url) => {
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `report_${deviceId}_${startDate}_${endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const triggerBlobDownload = async (res) => {
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    triggerDownload(url);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const downloadCompletedReport = async (reportId) => {
    const res = await authFetch(`${API}/reports/download/${reportId}`);
    const data = await res.json();

    if (!res.ok || !data.downloadUrl) {
      throw new Error(data.error || "Unable to prepare report download.");
    }

    triggerDownload(data.downloadUrl);
  };

  /* ── Main handler ── */
  const handleDownload = async () => {
    if (!isValid) return;
    setLoading(true);
    setStatusKey("preparing");

    try {
      /* ── Fast path: small date range ── */
      if (isSmallRange(startDate, endDate)) {
        setStatusKey("fetching");

        try {
          const res  = await authFetch(`${API}/reports/download-direct`, {
            method:  "POST",
            headers: { "Content-Type": "application/json" },
            body:    JSON.stringify({ deviceId, startDate, endDate }),
          });

          if (res.ok) {
            const contentType = res.headers.get("content-type") || "";
            if (contentType.includes("text/csv")) {
              await triggerBlobDownload(res);
              setStatusKey("ready");
              setLoading(false);
              return;
            }

            const data = await res.json();
            if (data.downloadUrl) {
              triggerDownload(data.downloadUrl);
              setStatusKey("ready");
              setLoading(false);
              return;
            }
          }
          // ✅ Direct path didn't work — fall through to async path gracefully
        } catch {
          // network error on direct path — fall through to async
        }
      }

      /* ── Async path: large range or direct failed ── */
      setStatusKey("processing");

      const createRes = await authFetch(`${API}/reports/create`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ deviceId, startDate, endDate }),
      });

      // ✅ Check createRes.ok — prevents polling /reports/status/undefined forever
      if (!createRes.ok) {
        let message = "Failed to create report.";
        try {
          const errData = await createRes.json();
          if (errData.error) message = errData.error;
        } catch {
          // ignore parse error
        }
        throw new Error(message);
      }

      const createData = await createRes.json();

      // ✅ Validate reportId exists before polling
      if (!createData.reportId) {
        throw new Error("Server did not return a report ID.");
      }

      const reportId = await pollStatus(createData.reportId);
      await downloadCompletedReport(reportId);
      setStatusKey("ready");

    } catch (err) {
      console.error("Report download error:", err);
      if (err === "timeout") {
        setStatusKey("timeout");
      } else {
        setStatusKey("failed");
      }
    }

    setLoading(false);
  };

  /* ── Cleanup on unmount ── */
  useEffect(() => () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  /* ── Derived ── */
  const cfg      = STATUS_CONFIG[statusKey];
  const statusLabel = STATUS_LABELS[statusKey];
  const dayDiff  = startDate && endDate
    ? Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24))
    : null;

  /* ════════════════════════════════════════
     RENDER
  ════════════════════════════════════════ */
  return (
    <div className="space-y-4">

      {/* ── Card ── */}
      <div className="bg-white border border-gray-100 rounded-xl p-6 space-y-5">

        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-[13px] font-semibold text-gray-800">Download Report</h2>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Export device telemetry as CSV for a custom date range
            </p>
          </div>
          <span className="text-[11px] font-medium text-gray-400 bg-gray-50 border border-gray-100
                           px-2.5 py-1 rounded-lg">
            CSV
          </span>
        </div>

        {/* Date inputs */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              max={endDate || today}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] text-gray-700
                         focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400
                         transition-colors bg-white"
            />
          </div>

          <div className="space-y-1.5">
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              min={startDate}
              max={today}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] text-gray-700
                         focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400
                         transition-colors bg-white"
            />
          </div>
        </div>

        {/* Range info */}
        {dayDiff !== null && dayDiff >= 0 && (
          <div className="flex items-center gap-2 text-[11px] text-gray-400">
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <span>
              {dayDiff === 0 ? "Same day selected" : `${dayDiff} day${dayDiff !== 1 ? "s" : ""} selected`}
              {dayDiff > 3 && (
                <span className="ml-1.5 text-amber-500 font-medium">
                  · Large range - Athena generation will be used
                </span>
              )}
            </span>
          </div>
        )}

        {/* Validation warning */}
        {startDate && endDate && new Date(startDate) > new Date(endDate) && (
          <p className="text-[11px] text-red-500 font-medium">
            Start date must be before end date
          </p>
        )}

        {/* Download button */}
        <button
          onClick={handleDownload}
          disabled={!isValid || loading}
          className={`
            w-full sm:w-auto flex items-center justify-center gap-2
            px-5 py-2.5 rounded-lg text-[13px] font-semibold transition-all duration-150
            ${!isValid || loading
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700 text-white active:scale-[0.98] shadow-sm"}
          `}
        >
          {loading ? (
            <>
              <span className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              Processing…
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5}
                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download CSV
            </>
          )}
        </button>

        {/* Status message */}
        {statusKey !== "idle" && statusLabel && (
          <div className={`flex items-center gap-2 text-[12px] font-medium px-3 py-2 rounded-lg border ${cfg.bg} ${cfg.color}`}>
            {loading && !["ready", "failed", "timeout"].includes(statusKey) ? (
              <span className="w-3 h-3 rounded-full border-2 border-current/30 border-t-current animate-spin flex-shrink-0" />
            ) : (
              <span className="flex-shrink-0">{cfg.icon}</span>
            )}
            {statusLabel}
          </div>
        )}

      </div>

      {/* ── Info note ── */}
      <div className="flex items-start gap-2 px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl">
        <span className="text-gray-300 text-base mt-0.5 flex-shrink-0">ℹ</span>
        <p className="text-[11px] text-gray-400 leading-relaxed">
          Reports for ranges up to 3 days are generated instantly.
          Larger ranges are generated with Athena and will download when ready.
          The download will start automatically when ready.
        </p>
      </div>

    </div>
  );
};

export default AdditionalTab;
